import React from 'react';
import { RiErrorWarningLine, RiInformationLine, RiShoppingCartLine } from 'react-icons/ri';
import ValidatedInput from '../ui/ValidatedInput';
import SearchableSelect from '../ui/SearchableSelect';
import { withCurrentOption } from '../../utils/withCurrentOption';
import { usePermission } from '../../hooks/usePermission';
import { Device, DeviceCondition, DeviceStatus } from '../../types/device';
import type { DeviceModel } from '../../services/deviceModelService';
import {
  DeviceForm,
  DeviceFormMode,
  DEVICE_UNIT_OPTIONS,
  isDeviceProcurementRequired,
  parseSerialNumbers,
} from './deviceForm';

/**
 * ช่องกรอกของฟอร์มอุปกรณ์ (เพิ่ม/แก้ไข) — ยกมาจาก renderDeviceForm ของโมดัลใน DevicePage ครบทุกช่อง
 * (หน้าเต็ม 28 ต.ค. 2026) · โหมดดูไม่ใช้ไฟล์นี้แล้ว (DeviceViewPage แสดงแบบอ่านอย่างเดียวเอง)
 *
 * setFormData แบบ functional ทุกจุด — ไม่ทับค่าที่ state อื่นเพิ่งเขียน (เช่น รหัสใหม่หลังชนรหัสซ้ำ)
 */
export interface DeviceFormFieldsProps {
  mode: DeviceFormMode;
  formData: DeviceForm;
  setFormData: React.Dispatch<React.SetStateAction<DeviceForm>>;
  formErrors: Record<string, string>;
  setFormErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  codeError: string;
  setCodeError: (msg: string) => void;
  deviceTypes: any[];
  brands: any[];
  suppliers: any[];
  deviceModels: DeviceModel[];
  loadingDeviceModels: boolean;
  /** เปลี่ยนยี่ห้อ — หน้าแม่ล้างรุ่น + โหลดรุ่นของยี่ห้อใหม่ (gate device_models.view) */
  onBrandChange: (value: number | string | null) => void;
  /** เครื่องตอนเปิดฟอร์ม (โหมดแก้ไข) — ใช้เติมค่าอ้างอิงที่ถูกปิดใช้งาน + กติกาบังคับกรอกจัดซื้อ */
  original?: Device | null;
}

const DeviceFormFields: React.FC<DeviceFormFieldsProps> = ({
  mode, formData, setFormData, formErrors, setFormErrors, codeError, setCodeError,
  deviceTypes, brands, suppliers, deviceModels, loadingDeviceModels, onBrandChange, original,
}) => {
  const { canView } = usePermission();
  const selectedDevice = original ?? null;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // ลบข้อผิดพลาดสำหรับฟิลด์นี้
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <div className="space-y-4">
      {/* ข้อมูลพื้นฐาน Section Header */}
      <div className="flex items-center gap-2 pb-2 border-b border-secondary-200">
        <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center shadow-sm">
          <RiInformationLine className="w-4 h-4 text-white" />
        </div>
        <h3 className="text-base font-semibold text-secondary-800">ข้อมูลพื้นฐาน</h3>
      </div>

      {/* แถว 1: รหัสอุปกรณ์ + ประเภทอุปกรณ์ */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <ValidatedInput
            label="รหัสอุปกรณ์"
            name="code"
            value={formData.code}
            onChange={(e) => {
              setFormData((prev) => ({ ...prev, code: e.target.value }));
              setCodeError('');
              if (formErrors.code) {
                setFormErrors((prev) => ({ ...prev, code: '' }));
              }
            }}
            error={formErrors.code}
            required
            disabled={mode === 'create'}
            placeholder={mode === 'create' ? 'รหัสจะสร้างอัตโนมัติ' : 'ใส่รหัสอุปกรณ์'}
          />
          {codeError && (
            <div className="mt-1 flex items-center text-sm text-danger-600">
              <RiErrorWarningLine className="w-4 h-4 mr-1" />
              {codeError}
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-secondary-700">ประเภทอุปกรณ์ <span className="text-danger-500">*</span></label>
          <SearchableSelect
            value={formData.device_type_id}
            onChange={(value) => setFormData((prev) => ({ ...prev, device_type_id: value }))}
            options={withCurrentOption(deviceTypes, mode === 'create' ? null : (selectedDevice as any)?.device_type)}
            optionValue="id"
            optionLabel="name"
            placeholder="เลือกประเภทอุปกรณ์"
            searchable={true}
            error={formErrors.device_type_id}
          />
        </div>
      </div>

      {/* แถว 2: ยี่ห้อ + รุ่น */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-secondary-700">ยี่ห้อ</label>
          <SearchableSelect
            value={formData.brand_id}
            onChange={(value) => onBrandChange(value)}
            // ไม่มีสิทธิ์โหลดรายการยี่ห้อ = ไม่รู้ว่าปิดใช้งานหรือไม่ → ห้ามติดป้าย "(ปิดใช้งาน)" (KD3 S2503)
            options={withCurrentOption(brands, mode === 'create' ? null : (selectedDevice as any)?.brand, canView('brands') ? undefined : '')}
            optionValue="id"
            optionLabel="name"
            placeholder="เลือกยี่ห้อ"
            searchable={true}
            error={formErrors.brand_id}
          />
          {formErrors.brand_id && <p className="mt-1 text-sm text-danger-600">{formErrors.brand_id}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-secondary-700">รุ่น</label>
          {formData.brand_id && deviceModels.length > 0 ? (
            <SearchableSelect
              value={formData.device_model_id}
              onChange={(value) => {
                const selectedModel = deviceModels.find((m) => m.id === value);
                setFormData((prev) => ({ ...prev, device_model_id: value, model: selectedModel?.name || '' }));
              }}
              options={deviceModels}
              optionValue="id"
              optionLabel="name"
              placeholder={loadingDeviceModels ? 'กำลังโหลด...' : 'เลือกรุ่น'}
              searchable={true}
              disabled={loadingDeviceModels}
              error={formErrors.device_model_id}
            />
          ) : (
            <input
              type="text"
              name="model"
              value={formData.model}
              onChange={handleInputChange}
              className={`mt-1 input ${formErrors.model ? 'border-danger-500' : ''}`}
              placeholder={formData.brand_id ? 'ไม่มีรุ่นในระบบ - พิมพ์ชื่อรุ่น' : 'เลือกยี่ห้อก่อน หรือพิมพ์ชื่อรุ่น'}
              aria-label="รุ่น"
              aria-invalid={Boolean(formErrors.model || formErrors.device_model_id)}
              aria-describedby={(formErrors.model || formErrors.device_model_id) ? 'model-error' : undefined}
            />
          )}
          {(formErrors.model || formErrors.device_model_id) && (
            <p id="model-error" className="mt-1 text-sm text-danger-600">{formErrors.model || formErrors.device_model_id}</p>
          )}
        </div>
      </div>

      {/* แถว 3: ประเภทการจัดการ — เลือกแบบการ์ด เต็มความกว้าง */}
      <div>
        <label className="block text-sm font-medium text-secondary-700 mb-2">
          ประเภทการจัดการ <span className="text-danger-500">*</span>
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:max-w-xl">
          {([
            { val: true, title: 'ติดตาม Serial Number', desc: 'รายเครื่อง — มี S/N เฉพาะตัว' },
            { val: false, title: 'นับจำนวนคงคลัง', desc: 'นับเป็นจำนวน — ไม่ระบุ S/N' },
          ] as const).map((opt) => {
            const selected = formData.is_serialized === opt.val;
            return (
              <label
                key={String(opt.val)}
                className={`flex items-start gap-2.5 px-3.5 py-2.5 rounded-lg border transition-colors cursor-pointer ${selected ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500' : 'border-secondary-300 hover:border-secondary-400 bg-white'}`}
              >
                <input
                  type="radio"
                  name="is_serialized"
                  checked={selected}
                  onChange={() => setFormData((prev) => ({
                    ...prev,
                    is_serialized: opt.val,
                    // default หน่วยนับตามประเภทการจัดการ: นับจำนวนคงคลัง → "ชิ้น", ติดตาม S/N → "เครื่อง"
                    // เปลี่ยนเฉพาะเมื่อยังเป็นค่า default ของอีกโหมด เพื่อไม่ทับหน่วยที่ผู้ใช้เลือกเอง
                    unit: opt.val
                      ? (prev.unit === 'ชิ้น' ? 'เครื่อง' : prev.unit)
                      : (prev.unit === 'เครื่อง' ? 'ชิ้น' : prev.unit),
                  }))}
                  className="mt-0.5"
                />
                <span>
                  <span className={`block text-sm font-medium ${selected ? 'text-primary-700' : 'text-secondary-800'}`}>{opt.title}</span>
                  {/* KD3 S2512: secondary-500 บนพื้น primary-50 (การ์ดที่เลือก) contrast 4.4 < 4.5 */}
                  <span className="block text-xs text-secondary-600">{opt.desc}</span>
                </span>
              </label>
            );
          })}
        </div>
      </div>

      {/* แสดงฟิลด์ตามประเภทการจัดการ */}
      {formData.is_serialized ? (
        <div>
          {/* KD3 S2512: label ไม่ผูกกับช่อง → screen reader อ่านแค่ placeholder (axe label-title-only) */}
          <label htmlFor="device-serial_number" className="block text-sm font-medium text-secondary-700">
            Serial Number <span className="text-danger-500">*</span>
          </label>
          {mode === 'create' ? (
            <>
              <textarea
                id="device-serial_number"
                name="serial_number"
                rows={3}
                value={formData.serial_number}
                onChange={handleInputChange}
                className={`mt-1 input ${formErrors.serial_number ? 'border-danger-500' : ''}`}
                placeholder={'กรอกได้หลาย serial — คั่นด้วย , หรือขึ้นบรรทัดใหม่\nเช่น\nSN001\nSN002, SN003'}
                aria-invalid={Boolean(formErrors.serial_number)}
                aria-describedby={formErrors.serial_number ? 'serial_number-error' : undefined}
              />
              {(() => {
                const n = parseSerialNumbers(formData.serial_number).length;
                return n > 1 ? (
                  <p className="mt-1 text-xs text-info-600" data-testid="device-bulk-serial-hint">
                    จะสร้าง {n} เครื่อง — ระบบสร้างรหัสอุปกรณ์ให้อัตโนมัติแต่ละเครื่อง
                  </p>
                ) : (
                  <p className="mt-1 text-xs text-secondary-500">
                    กรอกหลาย serial พร้อมกันได้ (คั่นด้วย , หรือขึ้นบรรทัดใหม่)
                  </p>
                );
              })()}
            </>
          ) : (
            <input
              id="device-serial_number"
              type="text"
              name="serial_number"
              value={formData.serial_number}
              onChange={handleInputChange}
              className={`mt-1 input ${formErrors.serial_number ? 'border-danger-500' : ''}`}
              placeholder="ใส่หมายเลขซีเรียล"
              aria-invalid={Boolean(formErrors.serial_number)}
              aria-describedby={formErrors.serial_number ? 'serial_number-error' : undefined}
            />
          )}
          {formErrors.serial_number && (
            <p id="serial_number-error" className="mt-1 text-sm text-danger-600">{formErrors.serial_number}</p>
          )}

          {/* B-2 (E2E ชั้น B): ช่อง IMEI ตอนแก้ไข · ตอนสร้างหลาย serial กำหนด IMEI ต่อเครื่องไม่ได้
              (DeviceBulkCreate ไม่รับ imei โดยตั้งใจ) → ไปเติมทีหลังที่ฟอร์มแก้ไข
              แสดงเฉพาะเครื่องที่ติดตามด้วย Serial (ของนับจำนวนไม่มี IMEI รายเครื่อง)
              ⚠️ ยังคงกติกา C8: ส่ง key นี้ "เฉพาะตอนผู้ใช้แก้จริง" เท่านั้น (ดู buildDevicePayload) */}
          {mode !== 'create' && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-secondary-700">IMEI</label>
              <input
                aria-label="IMEI"
                type="text"
                name="imei"
                value={formData.imei}
                onChange={handleInputChange}
                maxLength={20}
                className={`mt-1 input ${formErrors.imei ? 'border-danger-500' : ''}`}
                placeholder="ไม่มีก็เว้นว่างได้ (เช่น สายชาร์จ/อุปกรณ์เสริม)"
                aria-invalid={Boolean(formErrors.imei)}
                aria-describedby={formErrors.imei ? 'imei-error' : 'imei-help'}
              />
              {formErrors.imei ? (
                <p id="imei-error" className="mt-1 text-sm text-danger-600">{formErrors.imei}</p>
              ) : (
                <p id="imei-help" className="mt-1 text-xs text-secondary-500">
                  ห้ามซ้ำกับเครื่องอื่น · เว้นว่างไว้ได้ถ้าอุปกรณ์ไม่มี IMEI
                </p>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-secondary-700">
              จำนวน <span className="text-danger-500">*</span>
            </label>
            <input
              aria-label="จำนวน"
              type="number"
              name="quantity"
              value={formData.quantity}
              onChange={(e) => setFormData((prev) => ({ ...prev, quantity: parseInt(e.target.value) || 0 }))}
              min="0"
              className={`mt-1 input ${formErrors.quantity ? 'border-danger-500' : ''}`}
              aria-invalid={Boolean(formErrors.quantity)}
              aria-describedby={formErrors.quantity ? 'quantity-error' : undefined}
            />
            {formErrors.quantity && (
              <p id="quantity-error" className="mt-1 text-sm text-danger-600">{formErrors.quantity}</p>
            )}
            {/* แก้ยอดตรงนี้ = PUT /devices/{id} ที่ backend เขียน ledger ADJUSTMENT ให้ (STOCK-P0) — ทางเดิมของโมดัล */}
            {mode === 'edit' && (
              <p className="mt-1 text-xs text-secondary-500">
                แก้ยอดตรงนี้ระบบบันทึกเป็น &quot;ปรับปรุงยอด&quot; ในประวัติสต็อก · รับของเข้า/ตัดของเสียแนะนำให้ใช้เมนู &quot;คลังอุปกรณ์&quot;
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-secondary-700">หน่วยนับ</label>
            <select aria-label="หน่วยนับ" name="unit" value={formData.unit} onChange={handleInputChange} className="mt-1 select">
              {DEVICE_UNIT_OPTIONS.map((u) => (
                <option key={u} value={u}>{u}</option>
              ))}
              {/* หน่วยเดิมนอกชุดมาตรฐาน (นำเข้า Excel) — ต้องมี option ไม่งั้น select เด้งไปตัวแรกแล้วบันทึกทับ */}
              {formData.unit && !(DEVICE_UNIT_OPTIONS as readonly string[]).includes(formData.unit) && (
                <option value={formData.unit}>{formData.unit}</option>
              )}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-secondary-700">จำนวนขั้นต่ำ</label>
            <input
              aria-label="จำนวนขั้นต่ำ"
              type="number"
              name="minimum_quantity"
              value={formData.minimum_quantity}
              onChange={(e) => setFormData((prev) => ({ ...prev, minimum_quantity: parseInt(e.target.value) || 0 }))}
              min="0"
              className="mt-1 input"
              placeholder="จำนวนขั้นต่ำที่ต้องมีในคลัง"
            />
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-secondary-700">สถานะ</label>
          <select
            aria-label="สถานะ"
            name="status"
            value={formData.status}
            onChange={handleInputChange}
            // Phase 2 คลังอุปกรณ์: ISSUED ถูกตั้งโดยใบเบิกเท่านั้น — ถ้าปล่อยให้แก้ที่นี่
            // ค่าจะไม่ตรงกับใบเบิกและไม่มีแถวใน ledger · ล็อกช่องไว้ให้ไปจัดการที่เมนูคลังอุปกรณ์
            // PO 2026-09-08: RESERVED ก็เหมือนกัน — ตั้งโดยใบงานเตรียมเครื่องเท่านั้น
            // แก้มือ = ใบงานยังคิดว่าถือของอยู่ แต่เครื่องกลับเข้า pool ให้สัญญาอื่นหยิบได้
            // PO 2026-09-08: ของนับจำนวนไม่มีสถานะ — backend ตอบ 400 ถ้าส่ง status มา
            // ล็อกช่องไว้เลย ผู้ใช้จะได้ไม่กดแล้วเจอ error (ของเสีย = ตัดยอดที่ปรับปรุงยอด)
            disabled={
              formData.status === DeviceStatus.ISSUED
              || formData.status === DeviceStatus.RESERVED
              || formData.is_serialized === false
            }
            className="select"
          >
            {/* ISSUED / RESERVED ไม่ใช่ตัวเลือกให้เลือกเอง แต่ต้องมี <option> รองรับค่าปัจจุบัน
                ไม่งั้น select จะแสดงว่าง แล้วกดบันทึกทับสถานะจริงโดยผู้ใช้ไม่รู้ตัว */}
            {formData.status === DeviceStatus.ISSUED && <option value="ISSUED">เบิกไปใช้ภายใน</option>}
            {formData.status === DeviceStatus.RESERVED && <option value="RESERVED">จองไว้ (ใบงานเตรียมเครื่อง)</option>}
            <option value="AVAILABLE">พร้อมใช้งาน</option>
            <option value="RENTED">กำลังใช้งาน</option>
            <option value="MAINTENANCE">ซ่อมบำรุง</option>
            <option value="DAMAGED">ชำรุด</option>
            <option value="LOST">สูญหาย</option>
            <option value="DISPOSED">จำหน่ายออก</option>
          </select>
          {formData.status === DeviceStatus.ISSUED && (
            <p className="mt-1 text-xs text-secondary-500">
              เครื่องนี้ถูกเบิกไปใช้ภายใน — เปลี่ยนสถานะได้ที่เมนู &quot;คลังอุปกรณ์ ›
              ใบเบิก-ยืมอุปกรณ์&quot; โดยบันทึกการรับคืนหรือยกเลิกใบเบิก
            </p>
          )}
          {formData.status === DeviceStatus.RESERVED && (
            <p className="mt-1 text-xs text-secondary-500">
              เครื่องนี้ถูกกันไว้ให้ใบงานเตรียมเครื่อง — ปล่อยของได้ที่เมนู &quot;การเช่า ›
              ใบงานเตรียมเครื่อง&quot; โดยถอดเครื่องออกจากใบงานหรือยกเลิกใบงาน
            </p>
          )}
          {formData.is_serialized === false && (
            <p className="mt-1 text-xs text-secondary-500">
              อุปกรณ์แบบนับจำนวนไม่มีสถานะ — ความถูกต้องอยู่ที่ยอดคงเหลืออย่างเดียว
              หากของชำรุด/สูญหาย ให้ตัดยอดออกที่เมนู &quot;คลังอุปกรณ์ › ปรับปรุงยอด&quot;
              พร้อมระบุเหตุผล
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-secondary-700">วันที่ซื้อ</label>
          <input type="date" aria-label="วันที่ซื้อ" name="purchase_date" value={formData.purchase_date} onChange={handleInputChange} className="mt-1 input" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-secondary-700">ตำแหน่งที่ตั้ง/สถานที่</label>
          <input
            aria-label="ตำแหน่งที่ตั้ง/สถานที่"
            type="text"
            name="location"
            value={formData.location}
            onChange={handleInputChange}
            className="mt-1 input"
            placeholder="เช่น คลังสินค้า, โครงการ ABC"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-secondary-700">ลำดับเครื่อง</label>
          <input
            aria-label="ลำดับเครื่อง"
            type="number"
            name="device_number"
            value={formData.device_number || ''}
            onChange={(e) => setFormData((prev) => ({ ...prev, device_number: e.target.value ? parseInt(e.target.value) : null }))}
            min="1"
            className="mt-1 input"
            placeholder="เช่น 1, 2, 3 (ลำดับเครื่องในโครงการ)"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-secondary-700">หมายเหตุตำแหน่ง</label>
          <input
            aria-label="หมายเหตุตำแหน่ง"
            type="text"
            name="location_note"
            value={formData.location_note}
            onChange={handleInputChange}
            className="mt-1 input"
            placeholder="เช่น ป้อมหน้า, ป้อมสกัด, ชั้น 2"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-secondary-700">หมายเหตุ</label>
          <textarea aria-label="หมายเหตุ" name="notes" value={formData.notes} onChange={handleInputChange} rows={3} className="mt-1 input" />
        </div>
      </div>

      {/* ข้อมูลการจัดซื้อ/ครุภัณฑ์ */}
      <div className="mt-6 pt-6 border-t border-secondary-200">
        <div className="flex items-center gap-2 pb-2 border-b border-secondary-200 mb-4">
          <div className="w-8 h-8 bg-gradient-to-br from-warning-500 to-warning-600 rounded-lg flex items-center justify-center shadow-sm">
            <RiShoppingCartLine className="w-4 h-4 text-white" />
          </div>
          <h3 className="text-base font-semibold text-secondary-800">ข้อมูลการจัดซื้อ</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            {/* ดอกจันต้องตรงกับกติกาจริง — ถ้าปล่อยค้างไว้ ป้ายจะบอกว่าบังคับทั้งที่บันทึกผ่านได้ = หลอกผู้ใช้ */}
            <label className="block text-sm font-medium text-secondary-700">
              ผู้จำหน่าย{isDeviceProcurementRequired(mode, selectedDevice?.supplier_id) && <span className="text-danger-500"> *</span>}
            </label>
            <SearchableSelect
              value={formData.supplier_id}
              onChange={(value) => setFormData((prev) => ({ ...prev, supplier_id: value }))}
              options={withCurrentOption(suppliers, mode === 'create' ? null : (selectedDevice as any)?.supplier, canView('suppliers') ? undefined : '')}
              optionValue="id"
              optionLabel="name"
              placeholder="เลือกผู้จำหน่าย"
              searchable={true}
              error={formErrors.supplier_id}
            />
          </div>

          <div>
            <label htmlFor="device-purchase_price" className="block text-sm font-medium text-secondary-700">
              ราคาซื้อ (บาท){isDeviceProcurementRequired(mode, selectedDevice?.purchase_price) && <span className="text-danger-500"> *</span>}
            </label>
            <input
              id="device-purchase_price"
              type="number"
              name="purchase_price"
              // `??` — ราคา 0 บาทต้องแสดง 0 ไม่ใช่ช่องว่าง (โมดัลเดิมใช้ `|| ''` ทำให้ 0 หายตอนแก้ไข)
              value={formData.purchase_price ?? ''}
              onChange={(e) => {
                setFormData((prev) => ({ ...prev, purchase_price: e.target.value ? parseFloat(e.target.value) : null }));
                if (formErrors.purchase_price) {
                  setFormErrors((prev) => ({ ...prev, purchase_price: '' }));
                }
              }}
              min="0"
              step="0.01"
              className={`mt-1 input ${formErrors.purchase_price ? 'border-danger-500' : ''}`}
              placeholder="ราคาต้นทุน"
              aria-invalid={Boolean(formErrors.purchase_price)}
              aria-describedby={formErrors.purchase_price ? 'purchase_price-error' : undefined}
            />
            {formErrors.purchase_price && (
              <p id="purchase_price-error" className="mt-1 text-sm text-danger-600">{formErrors.purchase_price}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="block text-sm font-medium text-secondary-700">เลขที่ใบกำกับภาษี</label>
            <input
              aria-label="เลขที่ใบกำกับภาษี"
              type="text"
              name="invoice_number"
              value={formData.invoice_number}
              onChange={handleInputChange}
              className="mt-1 input"
              placeholder="เลขที่ใบกำกับ"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-secondary-700">วันที่รับเข้าคลัง</label>
            <input type="date" aria-label="วันที่รับเข้าคลัง" name="receive_date" value={formData.receive_date} onChange={handleInputChange} className="mt-1 input" />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="block text-sm font-medium text-secondary-700">สภาพสินค้า</label>
            <select
              aria-label="สภาพสินค้า"
              name="condition"
              value={formData.condition || ''}
              onChange={(e) => setFormData((prev) => ({ ...prev, condition: (e.target.value as DeviceCondition) || null }))}
              className="select"
            >
              <option value="">-- เลือกสภาพ --</option>
              <option value={DeviceCondition.NEW}>ใหม่</option>
              <option value={DeviceCondition.USED}>มือสอง</option>
              <option value={DeviceCondition.REFURBISHED}>ซ่อมแล้ว</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-secondary-700">มูลค่าทางบัญชี (บาท)</label>
            <input
              aria-label="มูลค่าทางบัญชี (บาท)"
              type="number"
              name="book_value"
              value={formData.book_value || ''}
              onChange={(e) => setFormData((prev) => ({ ...prev, book_value: e.target.value ? parseFloat(e.target.value) : null }))}
              min="0"
              step="0.01"
              className="mt-1 input"
              placeholder="มูลค่าทางบัญชี"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="block text-sm font-medium text-secondary-700">เลขครุภัณฑ์</label>
            <input
              aria-label="เลขครุภัณฑ์"
              type="text"
              name="asset_number"
              value={formData.asset_number}
              onChange={handleInputChange}
              className="mt-1 input"
              placeholder="รหัสครุภัณฑ์/ทรัพย์สิน"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeviceFormFields;
